import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, Navigate } from "react-router-dom";
import axios from "axios";
import { getToken, getAuthUser, isAuthenticated } from "../auth";

const API = "https://api.vyapakprint.com/api";

const OrderStatus = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const urlStatus = new URLSearchParams(location.search).get("status") || "All";

  const [orders, setOrders] = useState([]);
  const [filter, setFilter] = useState(urlStatus);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [cancellingOrder, setCancellingOrder] = useState(null);
  const [modalImage, setModalImage] = useState(null);
  const [cancelRemark, setCancelRemark] = useState("");
  const [showRemarkModal, setShowRemarkModal] = useState(false);
  const [pendingCancelOrderId, setPendingCancelOrderId] = useState(null);
  const [distributionId, setDistributionId] = useState(null);
  const [downloadingFile, setDownloadingFile] = useState(null);

  const auth = useMemo(() => getAuthUser(), []);
  const token = useMemo(() => getToken(), []);

  useEffect(() => {
    setFilter(urlStatus);
  }, [urlStatus]);

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        setLoading(true);
        setError("");
        setSuccess("");

        if (!token || !isAuthenticated()) {
          throw new Error("Please log in again.");
        }

        const storedUser = auth?.storedUser || null;

        const email = auth?.email || storedUser?.email || null;

        const safeDistributionId =
          auth?.distributionId ||
          auth?.authUserId ||
          storedUser?.distributionId ||
          storedUser?.id ||
          storedUser?._id ||
          null;

        if (storedUser) {
          setDistributionId(
            storedUser?.distributionId ||
              storedUser?.id ||
              storedUser?._id ||
              null,
          );
        }

        const distributionRes = await fetch(`${API}/distribution/`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!distributionRes.ok) {
          throw new Error(`HTTP ${distributionRes.status}`);
        }

        const distributionData = await distributionRes.json();

        let distributionList = [];
        if (Array.isArray(distributionData)) {
          distributionList = distributionData;
        } else if (Array.isArray(distributionData?.data)) {
          distributionList = distributionData.data;
        } else if (Array.isArray(distributionData?.distributions)) {
          distributionList = distributionData.distributions;
        }

        const matchedUser =
          distributionList.find(
            (item) =>
              safeDistributionId &&
              String(item?._id || item?.id) === String(safeDistributionId),
          ) ||
          distributionList.find(
            (item) =>
              email &&
              String(item?.email || "").toLowerCase() ===
                String(email).toLowerCase(),
          ) ||
          storedUser;

        const resolvedDistributionId =
          matchedUser?._id || matchedUser?.id || safeDistributionId || null;

        if (!resolvedDistributionId) {
          throw new Error("Distribution application not found.");
        }

        setDistributionId(resolvedDistributionId);

        const ordersRes = await fetch(
          `${API}/businessOrders/getBusinessOrdersByUserId/${resolvedDistributionId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );

        if (!ordersRes.ok) {
          throw new Error(`HTTP ${ordersRes.status}`);
        }

        const ordersData = await ordersRes.json();

        const businessOrders = Array.isArray(ordersData?.BusinessOrders)
          ? ordersData.BusinessOrders
          : Array.isArray(ordersData?.data)
            ? ordersData.data
            : Array.isArray(ordersData)
              ? ordersData
              : [];

        const sortedOrders = [...businessOrders].sort((a, b) => {
          const aTime = a?.orderDate ? new Date(a.orderDate).getTime() : 0;
          const bTime = b?.orderDate ? new Date(b.orderDate).getTime() : 0;
          return bTime - aTime;
        });

        setOrders(sortedOrders);
      } catch (err) {
        console.error("Order status fetch error:", err);
        setError(err?.message || "Unable to load orders.");
      } finally {
        setLoading(false);
      }
    };

    fetchOrders();
  }, [auth, token]);

  const closeCancelModal = () => {
    setShowRemarkModal(false);
    setCancelRemark("");
    setPendingCancelOrderId(null);
  };

  const changeStatus = async (orderId, newStatus, remark = "") => {
    setCancellingOrder(orderId);
    setError("");
    setSuccess("");

    try {
      if (!token || !isAuthenticated()) {
        throw new Error("Please log in again.");
      }

      if (newStatus === "Cancelled") {
        await axios.patch(
          `${API}/businessOrders/${orderId}/cancel`,
          { remark },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );
      } else {
        await axios.put(
          `${API}/businessOrders/updateBusinessOrderStatus/${orderId}`,
          { status: newStatus },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );
      }

      setOrders((prevOrders) =>
        prevOrders.map((order) =>
          order._id === orderId
            ? {
                ...order,
                status: newStatus,
                statusHistory:
                  newStatus === "Cancelled"
                    ? [
                        ...(order.statusHistory || []),
                        {
                          changedAt: new Date().toISOString(),
                          remark,
                          image: null,
                          status: "Cancelled",
                        },
                      ]
                    : order.statusHistory,
              }
            : order,
        ),
      );

      setSuccess(
        newStatus === "Cancelled"
          ? "Order cancelled and amount refunded."
          : "Order status updated successfully.",
      );
    } catch (e) {
      console.error("Status update error:", e);
      setError(
        e?.response?.data?.message ||
          e?.message ||
          "Failed to update order status. Please try again.",
      );
    } finally {
      setCancellingOrder(null);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";

    return new Date(dateString).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return "N/A";

    return new Date(dateString).toLocaleString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  const getLastStatusHistory = (statusHistory) => {
    if (!Array.isArray(statusHistory) || statusHistory.length === 0) {
      return null;
    }
    return statusHistory[statusHistory.length - 1];
  };

  const getFileNameFromUrl = (url) => {
    if (!url) return "order-file";
    try {
      const cleanUrl = url.split("?")[0];
      const fileName = cleanUrl.split("/").pop();
      return fileName || "order-file";
    } catch {
      return "order-file";
    }
  };

  const handleDownloadFile = async (url, filename, orderId) => {
    if (!url) return;

    try {
      setDownloadingFile(orderId);

      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`Failed to download file: ${response.status}`);
      }

      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = filename || "order-file";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      console.error("Download failed:", err);
      setError("Failed to download file.");
    } finally {
      setDownloadingFile(null);
    }
  };

  const statusOptions = useMemo(
    () => ["All", ...Array.from(new Set(orders.map((o) => o.status))).sort()],
    [orders],
  );

  const visible = useMemo(() => {
    return filter === "All"
      ? orders
      : orders.filter((o) => String(o.status) === String(filter));
  }, [orders, filter]);

  if (!isAuthenticated()) {
    return <Navigate to="/login" replace />;
  }

  return (
    <div className="container mx-auto min-h-screen p-4 md:p-6">
      <h1 className="mb-6 text-3xl font-bold">Order Status</h1>

      {error ? (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-4 text-red-600">
          {error}
        </div>
      ) : null}

      {success ? (
        <div className="mb-4 rounded-lg border border-green-200 bg-green-50 p-4 text-green-700">
          {success}
        </div>
      ) : null}

      {showRemarkModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-lg">
            <h3 className="mb-4 text-lg font-semibold">Cancel Order</h3>

            <label className="mb-2 block text-sm font-medium text-gray-700">
              Please provide a reason for cancellation:
            </label>

            <textarea
              className="mb-4 w-full rounded-lg border border-gray-300 p-2"
              rows={4}
              value={cancelRemark}
              onChange={(e) => setCancelRemark(e.target.value)}
            />

            <div className="flex justify-end gap-4">
              <button
                className="rounded bg-gray-200 px-4 py-2 text-gray-800 hover:bg-gray-300"
                onClick={closeCancelModal}
                type="button"
              >
                Close
              </button>

              <button
                disabled={!cancelRemark.trim()}
                className={`rounded px-4 py-2 ${
                  !cancelRemark.trim()
                    ? "cursor-not-allowed bg-gray-300 text-gray-500"
                    : "bg-red-500 text-white hover:bg-red-600"
                }`}
                onClick={() => {
                  changeStatus(
                    pendingCancelOrderId,
                    "Cancelled",
                    cancelRemark.trim(),
                  );
                  closeCancelModal();
                }}
                type="button"
              >
                Confirm Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="mb-6 rounded-lg border bg-white p-4 shadow">
        <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
          <div className="w-full md:max-w-sm">
            <label className="mb-2 block text-sm font-medium text-gray-700">
              Filter by Status
            </label>

            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="w-full rounded-md border px-3 py-2 focus:outline-none focus:ring-1 focus:ring-blue-500"
            >
              {statusOptions.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </div>

          {distributionId ? (
            <div className="text-xs text-gray-500">
              Account ID: {distributionId}
            </div>
          ) : null}
        </div>
      </div>

      <div className="overflow-auto rounded-lg border bg-white shadow">
        {loading ? (
          <p className="p-6 text-center">Loading...</p>
        ) : visible.length ? (
          <table className="min-w-[1600px] w-full text-sm">
            <thead className="bg-gray-100">
              <tr>
                <th className="px-4 py-3 text-left">#</th>
                <th className="px-4 py-3 text-left">Order ID</th>
                <th className="px-4 py-3 text-left">Date</th>
                <th className="px-4 py-3 text-center">Status</th>
                <th className="px-4 py-3 text-center">Remarks</th>
                <th className="px-4 py-3 text-left">Firm Name</th>
                <th className="px-4 py-3 text-left">Contact Person</th>
                <th className="px-4 py-3 text-center">Quantity</th>
                <th className="px-4 py-3 text-center">Total (₹)</th>
                <th className="px-4 py-3 text-center">File</th>
                <th className="px-4 py-3 text-center">Last Updated</th>
                <th className="px-4 py-3 text-center">Actions</th>
              </tr>
            </thead>

            <tbody>
              {visible.map((order, i) => {
                const lastHistory = getLastStatusHistory(order.statusHistory);
                const lastUpdate = lastHistory?.changedAt || null;
                const remarks = lastHistory?.remark || null;
                const statusImage = lastHistory?.image || null;
                const fileName = getFileNameFromUrl(order.file);

                return (
                  <tr key={order._id} className="border-t hover:bg-gray-50">
                    <td className="px-4 py-3">{i + 1}</td>

                    <td className="px-4 py-3 font-mono text-xs">
                      {order._id || "N/A"}
                    </td>

                    <td className="px-4 py-3">{formatDate(order.orderDate)}</td>

                    <td className="px-4 py-3 text-center">
                      <span
                        className={`rounded-full px-2 py-1 text-xs font-medium ${
                          order.status === "Pending"
                            ? "bg-yellow-100 text-yellow-800"
                            : order.status === "Cancelled"
                              ? "bg-red-100 text-red-800"
                              : order.status === "Dispatched"
                                ? "bg-green-100 text-green-800"
                                : order.status === "Printing"
                                  ? "bg-blue-100 text-blue-800"
                                  : order.status === "Packing"
                                    ? "bg-purple-100 text-purple-800"
                                    : "bg-gray-100 text-gray-800"
                        }`}
                      >
                        {order.status}
                      </span>
                    </td>

                    <td className="px-4 py-3 text-center text-xs">
                      <div className="flex items-center justify-center gap-2">
                        <span>{remarks || "N/A"}</span>

                        {statusImage ? (
                          <button
                            onClick={() => setModalImage(statusImage)}
                            className="text-xs text-blue-500 underline hover:text-blue-700"
                            type="button"
                          >
                            View Image
                          </button>
                        ) : null}
                      </div>
                    </td>

                    <td className="px-4 py-3">{order.firmName || "N/A"}</td>

                    <td className="px-4 py-3">
                      {order.userDetails?.contactPerson || "N/A"}
                    </td>

                    <td className="px-4 py-3 text-center">
                      {order.quantity || 0}
                    </td>

                    <td className="px-4 py-3 text-center font-semibold">
                      ₹{Number(order.totalPrice || 0)}
                    </td>

                    <td className="px-4 py-3 text-center text-xs">
                      {order.file ? (
                        <div className="flex flex-col items-center gap-2">
                          <span
                            className="max-w-[220px] truncate text-gray-700"
                            title={fileName}
                          >
                            {fileName}
                          </span>

                          <div className="flex items-center justify-center gap-2">
                            <a
                              href={order.file}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="rounded bg-blue-500 px-3 py-1 text-white transition hover:bg-blue-600"
                            >
                              View
                            </a>

                            <button
                              type="button"
                              onClick={() =>
                                handleDownloadFile(
                                  order.file,
                                  fileName,
                                  order._id,
                                )
                              }
                              disabled={downloadingFile === order._id}
                              className={`rounded px-3 py-1 text-white transition ${
                                downloadingFile === order._id
                                  ? "cursor-not-allowed bg-gray-400"
                                  : "bg-green-500 hover:bg-green-600"
                              }`}
                            >
                              {downloadingFile === order._id
                                ? "Downloading..."
                                : "Download"}
                            </button>
                          </div>
                        </div>
                      ) : (
                        <span className="text-gray-400">N/A</span>
                      )}
                    </td>

                    <td className="px-4 py-3 text-center text-xs">
                      {lastUpdate ? formatDateTime(lastUpdate) : "N/A"}
                    </td>

                    <td className="px-4 py-3 text-center">
                      <div className="flex flex-col items-center gap-2">
                        <button
                          onClick={() =>
                            navigate(`/order-details/${order._id}`, {
                              state: { order },
                            })
                          }
                          className="rounded bg-indigo-500 px-3 py-1 text-xs font-medium text-white transition hover:bg-indigo-600"
                          type="button"
                        >
                          View Details
                        </button>

                        {order.status === "Pending" ? (
                          <button
                            onClick={() => {
                              setPendingCancelOrderId(order._id);
                              setShowRemarkModal(true);
                            }}
                            disabled={cancellingOrder === order._id}
                            className={`rounded px-3 py-1 text-xs font-medium transition-colors ${
                              cancellingOrder === order._id
                                ? "cursor-not-allowed bg-gray-300 text-gray-500"
                                : "bg-red-500 text-white hover:bg-red-600"
                            }`}
                            type="button"
                          >
                            {cancellingOrder === order._id
                              ? "Cancelling..."
                              : "Cancel Order"}
                          </button>
                        ) : (
                          <span className="text-xs text-gray-400">—</span>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        ) : (
          <p className="p-6 text-center text-gray-600">
            No orders found for the selected status.
          </p>
        )}
      </div>

      {!loading && visible.length > 0 ? (
        <div className="mt-4 text-sm text-gray-600">
          Showing {visible.length} of {orders.length} orders
        </div>
      ) : null}

      {modalImage ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="max-h-[90vh] w-full max-w-4xl overflow-auto rounded-lg bg-white p-4">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-lg font-semibold">Status Image</h3>

              <button
                onClick={() => setModalImage(null)}
                className="text-xl font-bold text-gray-500 hover:text-gray-700"
                type="button"
              >
                ×
              </button>
            </div>

            <img
              src={modalImage}
              alt="Status update"
              className="mx-auto max-h-[70vh] max-w-full object-contain"
            />
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default OrderStatus;
